
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useState } from 'react';
import { useArtists } from '../../hooks/useArtists';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ArtistDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { getArtistBySlug } = useArtists();
  const artist = getArtistBySlug(slug || '');
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState('전체');

  if (!artist) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-6">
        <h2 className="text-4xl font-bold">Artist Not Found</h2>
        <p className="text-gray-600">The artist you are looking for does not exist.</p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-3 bg-black text-white rounded-full text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          Back to Home
        </button>
      </div>
    );
  }

  const typeLabel =
    artist.type === 'illustrator'
      ? 'Illustrators'
      : artist.type === 'designer'
      ? 'Designers'
      : 'Writers';
  const typePath =
    artist.type === 'illustrator'
      ? '/illustrators'
      : artist.type === 'designer'
      ? '/designers'
      : '/writers';

  const categories = [
    '전체',
    ...Array.from(new Set(artist.portfolio.map((p) => p.category))),
  ];
  const filteredWorks =
    activeCategory === '전체'
      ? artist.portfolio
      : artist.portfolio.filter((p) => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-24 pb-4 px-6 md:px-8 max-w-6xl mx-auto">
        <nav className="flex items-center gap-2 text-sm text-gray-400">
          <Link to="/" className="hover:text-black transition-colors cursor-pointer">
            Home
          </Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
          <Link to={typePath} className="hover:text-black transition-colors cursor-pointer">
            {typeLabel}
          </Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
          <span className="text-gray-700 font-medium">{artist.name}</span>
        </nav>
      </div>

      {/* Artist Profile Section */}
      <section className="px-6 md:px-8 pt-4 pb-16 md:pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-10 md:gap-16 items-start">
            {/* Profile Image */}
            <div className="w-full md:w-72 lg:w-80 flex-shrink-0">
              <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-gray-100">
                <img
                  src={artist.profileImage}
                  alt={artist.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Profile Info */}
            <div className="flex-1 pt-0 md:pt-2">
              <span className="text-xs font-semibold tracking-[0.2em] text-gray-400 uppercase mb-3 block">
                {artist.type === 'illustrator'
                  ? '일러스트레이터'
                  : artist.type === 'designer'
                  ? '디자이너'
                  : '작가'}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-3 leading-tight">
                {artist.name}
              </h1>
              <p className="text-lg text-gray-500 italic mb-4 font-light">{artist.specialty}</p>
              <p className="text-base text-gray-600 leading-relaxed mb-6">{artist.tagline}</p>

              {/* Bio */}
              {artist.bio && artist.bio.length > 0 && (
                <div className="space-y-4 mb-8">
                  {artist.bio.map((paragraph, index) => (
                    <p key={index} className="text-sm text-gray-500 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              )}

              {/* Social Links */}
              <div className="flex items-center gap-3">
                {artist.social.instagram && (
                  <a
                    href={artist.social.instagram}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-full text-sm text-gray-600 hover:border-black hover:text-black transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-instagram-line text-base"></i>
                    Instagram
                  </a>
                )}
                {artist.social.website && (
                  <a
                    href={artist.social.website}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-full text-sm text-gray-600 hover:border-black hover:text-black transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-global-line text-base"></i>
                    Website
                  </a>
                )}
                {artist.social.twitter && (
                  <a
                    href={artist.social.twitter}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-full text-sm text-gray-600 hover:border-black hover:text-black transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-twitter-x-line text-base"></i>
                    Twitter
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-6xl mx-auto px-6 md:px-8">
        <div className="border-t border-gray-100"></div>
      </div>

      {/* Portfolio Section */}
      {artist.portfolio.length > 0 && (
        <section id="gallery" className="py-16 md:py-20 px-6 md:px-8">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-10">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold">포트폴리오</h2>
                <p className="text-gray-400 text-sm mt-1.5">{artist.portfolio.length}개 작품</p>
              </div>

              {/* Filter Tabs */}
              {categories.length > 2 && (
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                        activeCategory === cat
                          ? 'bg-black text-white'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Gallery Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredWorks.map((work) => (
                <div
                  key={work.id}
                  className="group cursor-pointer"
                  onClick={() => setLightboxImage(work.image)}
                >
                  <div className="relative overflow-hidden rounded-xl bg-gray-100 aspect-[4/5] mb-3">
                    <img
                      src={work.image}
                      alt={work.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                      <div className="w-11 h-11 flex items-center justify-center bg-white rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 scale-75 group-hover:scale-100">
                        <i className="ri-zoom-in-line text-lg text-black"></i>
                      </div>
                    </div>
                  </div>
                  <h4 className="text-sm font-semibold mb-0.5">{work.title}</h4>
                  <p className="text-xs text-gray-400">
                    {work.category} &middot; {work.year}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Back CTA */}
      <section className="pb-20 px-6 md:px-8">
        <div className="max-w-6xl mx-auto text-center">
          <Link
            to={typePath}
            className="inline-flex items-center gap-2 px-7 py-3.5 border border-gray-300 text-sm font-semibold rounded-full hover:border-black transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            전체{' '}
            {artist.type === 'illustrator'
              ? '일러스트레이터'
              : artist.type === 'designer'
              ? '디자이너'
              : '작가'}{' '}
            보기
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6 md:p-8 cursor-pointer"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center bg-white/10 rounded-full hover:bg-white/20 transition-colors cursor-pointer"
            onClick={() => setLightboxImage(null)}
          >
            <i className="ri-close-line text-white text-xl"></i>
          </button>
          <img
            src={lightboxImage}
            alt="Portfolio work"
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ArtistDetailPage;
